// Copyright © 2017-2020  SDG Systems, LLC. All rights reserved.
// This file may not be used in any form without the express written permission of SDG Systems, LLC.

<template>
  <v-container fluid grid-list-md>
    <v-row wrap>
      <v-col cols="12" justify-center>
        <v-card>
          <v-progress-linear :indeterminate="loading" style="margin-top: 0px;"></v-progress-linear>
          <v-card-text>
            <v-container fluid style="padding: 0px;">
              <v-row wrap>
                <v-col cols="12" md="4">
                  <date-time-view
                  v-model="dateStart"
                  clearable
                  label="Start Date"
                  ></date-time-view>
                </v-col>
                <v-col cols="12" md="8">
                  <v-btn dark color="blue darken-1" class="mx-1" @click="resetDate()">Today</v-btn>
                  <v-btn dark color="blue darken-2" class="mx-1" @click="resetWeek()">Last 7 Days</v-btn>
                  <v-btn dark color="blue darken-3" class="mx-1" @click="resetMonth()">Last 30 Days</v-btn>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-btn v-on="on" icon @click="showGraph = !showGraph">
                        <v-icon v-if="showGraph">view_agenda</v-icon>
                        <v-icon v-else>equalizer</v-icon>
                      </v-btn>
                    </template>
                    <span v-if="showGraph">Show Data</span>
                    <span v-else>Show Graph</span>
                  </v-tooltip>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
        </v-card>
      </v-col>
      <template v-if="!showGraph">
        <v-col cols="12" v-for="(zone, i) in zones" :key="i" style="padding:0px;">
          <v-card v-if="zone.things.length > 0" style="margin:4px;">
            <v-card-title>
              <span class="title mr-4">Zone: {{ zone.name }} </span>
              <span v-if="zone.duration > 0">Max Time: {{ getZoneDurationMinutes(zone.duration) }} minutes</span>
            </v-card-title>
            <v-card-text style="padding-top: 0px;">
            <v-data-table
              v-bind:headers="headers"
              :items="zone.things"
              :pagination-options.sync="pagination"
              :items-per-page="25"
              :footer-props="{
                'items-per-page-options': [10, 25, 100]
              }">
                <template v-slot:item="props">
                  <tr :class="getRowStyle(props.item.state.lastZoneTime, zone.duration)">
                    <td class="text-xs-left">
                      {{ props.item.common.name }}
                    </td>
                    <td class="text-xs-left">
                      {{ getThingTags(props.item) }}
                    </td>
                    <td class="text-xs-left">
                      {{ formatTime(props.item.state.lastZoneTime) }} - {{ getTimeIn(props.item.state.lastZoneTime) }}
                    </td>
                    <td class="text-xs-left">
                      {{ getEndpointName(props.item.state.endpoint) }}
                    </td>
                    <td class="text-xs-left">
                      {{ props.item.state.direction }}
                    </td>
                  </tr>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </template>
      <v-col cols="12" v-else>
        <v-card>
          <v-card-text>
            <chart-bar-horizontal :chart-data="chartData" :options="chartOptions"></chart-bar-horizontal>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  <div id="eventZonesCanaryElement"></div>
  </v-container>
</template>

<script>
import moment from 'moment'

import ApiRequests from '@/api/ApiRequests'
import { mapGetters } from 'vuex'
import ChartBarHorizontal from '@/components/ChartBarHorizontal.js'
import DateTimeView from '@/components/DateTimeView'
import {LookupMixin, TimeMixin, SettingsMixin} from '@/shared/mixins'

export default {
  components: {
    ChartBarHorizontal,
    DateTimeView
  },

  mixins: [LookupMixin, TimeMixin, SettingsMixin],

  data () {
    return {
      headers: [],
      chartData: {},
      chartOptions: {
        maintainAspectRatio: false
      },
      dateStart: moment().startOf('day'),
      endpoints: [],
      loading: true,
      now: moment(),
      pagination: {rowsPerPage: 10},
      showGraph: false,
      things: [],
      zones: []
    }
  },

  created () {
    if (Object.keys(this.siteSettings).length > 0) this.createHeader()
  },

  mounted () {
    this.getEndpoints()
    this.getZones().then(() => {
      this.poll(this.getThings, 5000)
      this.poll(() => {
        this.now = moment()
      }, 1000)
    })
  },

  computed: {
    ...mapGetters([
      'selectedSite',
      'siteSettings'
    ])
  },

  watch: {
    selectedSite: function () {
      this.getEndpoints()
      this.getZones().then(() => {
        this.getThings()
      })
    },

    siteSettings: function () {
      this.createHeader()
    },

    dateStart: function () {
      this.getThings()
    }
  },

  methods: {
    createHeader: function () {
      this.headers = [
        {
          text: this.getItemNameLabel(this.siteSettings.itemNameLabel),
          align: 'left',
          value: 'workername',
          sortable: false
        },
        {
          text: 'Tag',
          align: 'left',
          value: 'tag',
          sortable: false
        },
        {
          text: 'Duration',
          align: 'left',
          value: 'time',
          sortable: false
        },
        {
          text: 'Endpoint',
          align: 'left',
          value: 'item.endpoint',
          sortable: false
        },
        {
          text: 'Direction',
          align: 'left',
          value: 'direction',
          sortable: false
        }
      ]
    },

    fillData () {
      var data = []
      var dataItems = []
      var dataWorkers = []
      var labels = []

      for (var i in this.zones) {
        var d = this.zones[i]
        data.push({
          x: d.things.length,
          y: i
        })
        dataItems.push({
          x: d.items.length,
          y: i
        })
        dataWorkers.push({
          x: d.workers.length,
          y: i
        })
        labels.push(d.name)
      }

      this.chartOptions = {}
      this.chartOptions = {
        maintainAspectRatio: false,
        scales: {
          xAxes: [
            {
              ticks: {
                callback: function (value, index, values) {
                  if (value % 1 === 0) { return value }
                },
                suggestedMin: 0
              }
            }
          ],
          yAxes: [
            {}
          ]
        }
      }
      this.chartData = {
        labels: labels,
        datasets: [
          {
            label: this.getItemLabel(),
            borderColor: 'rgba(226, 184, 104, 1.0)',
            borderWidth: 2,
            backgroundColor: 'rgba(226, 184, 104, 0.5)',
            data: dataItems
          }
        ]
      }
      if (!this.siteSettings.hideWorkers) {
        let obj = {
          label: 'Total',
          borderColor: 'rgba(118, 149, 185, 1.0)',
          borderWidth: 2,
          backgroundColor: 'rgba(118, 149, 185, 0.5)',
          data: data
        }
        this.chartData.datasets.unshift(obj)
        obj = {
          label: this.getWorkerLabel(),
          borderColor: 'rgba(127, 141, 104, 1.0)',
          borderWidth: 2,
          backgroundColor: 'rgba(127, 141, 104, 0.5)',
          data: dataWorkers
        }
        this.chartData.datasets.push(obj)
      }
    },

    getItemLabel: function () {
      if (this.siteSettings.itemLabel === '') return 'Items'
      return this.siteSettings.itemLabel
    },

    getWorkerLabel: function () {
      if (this.siteSettings.workerLabel === '') return 'Workers'
      return this.siteSettings.workerLabel
    },

    getEndpoints: function () {
      ApiRequests.getEndpoints(this.selectedSite._id).then(response => {
        this.endpoints = response.data
      })
    },

    getEndpointName: function (id) {
      var endpoint = this.endpoints.filter(e => e._id === id)[0]
      if (endpoint) {
        return endpoint.name
      }
      return 'N/A'
    },

    getThings: function () {
      this.loading = true
      var params = {
        'site': this.selectedSite._id,
        'sort': 'name'
      }
      if (this.dateStart) {
        params.after = moment(this.dateStart).format()
      }

      ApiRequests.thingQuery(params).then(r => {
        this.things = r.data
        for (var i = 0; i < this.zones.length; i++) {
          var zone = this.zones[i]
          zone.things = this.things.filter(t => t.state.zone === this.zones[i]._id)
          zone.workers = zone.things.filter(t => t.common.class === 'worker')
          zone.items = zone.things.filter(t => t.common.class === 'item')
          this.$set(this.zones, i, zone)
        }
        this.fillData()
        this.loading = false
      })
    },

    getRowStyle: function (time, maxtime) {
      var duration = this.getTimeDuration(time)
      var maxtimeminutes = this.getZoneDurationMinutes(maxtime)

      if (duration.as('minutes') > maxtimeminutes && maxtime > 0) {
        return 'red lighten-2'
      }
      return ''
    },

    getZones: function () {
      return ApiRequests.getZones(this.selectedSite._id, 'name').then(response => {
        var z = response.data
        for (var i = 0; i < z.length; i++) {
          z[i].things = []
        }
        this.zones = z
      })
    },

    getZoneDurationMinutes: function (duration) {
      return duration / (1000000000 * 60)
    },

    getTimeDuration: function (time) {
      return moment.duration(this.now.diff(moment(time)))
    },

    getTimeIn: function (time) {
      var duration = this.getTimeDuration(time)
      return Math.floor(duration.as('hours')) + ':' + this.pad(duration.minutes()) + ':' + this.pad(duration.seconds())
    },

    poll: function (fn, interval) {
      if (interval === null) {
        interval = 1000
      }
      var repeatFunc = function () {
        fn()
        // Use this to kill the poll if it's not visible.
        if (document.getElementById('eventZonesCanaryElement')) {
          setTimeout(repeatFunc, interval)
        }
      }
      repeatFunc()
    },

    resetDate: function () {
      this.dateStart = moment().startOf('day')
    },

    resetWeek: function () {
      this.dateStart = moment().startOf('day').subtract(6, 'days').format()
    },

    resetMonth: function () {
      this.dateStart = moment().startOf('day').subtract(29, 'days').format()
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/views/ReportZones.vue